/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { type ReactElement, type ReactNode, type PropsWithChildren, useRef, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router';
import { Link } from '@/components/link';
import Search from './search';
import CartBadge from './cart-badge';
import UserActions from './user-actions/user-actions';
import { useTranslation } from 'react-i18next';
import logo from '/images/logo.png';
import { Button } from '@/components/ui/button';
import { SparklesIcon } from '@/components/icons';
import { useConfig } from '@salesforce/storefront-next-runtime/config';
import { launchChat } from '@/components/shopper-agent';
import { validateShopperAgentConfig } from '@/components/shopper-agent/shopper-agent.utils';
import { UITarget } from '@/targets/ui-target';

interface HeaderProps extends PropsWithChildren {
    beforeHeader?: ReactNode;
    variant?: 'full' | 'checkout';
}

const DONATE_HREF = 'https://giftshop.stjude.org/gift-of-donation-pd.html';

// Isolates the `useLocation()` subscription so that route changes only re-render the Search component (via key reset)
// without cascading a re-render through the entire `Header` tree, which would unnecessarily re-render the navigation
// menu and other stable children.
function LocationKeyedSearch() {
    const location = useLocation();
    return <Search key={`${location.pathname}${location.search}`} />;
}

export default function Header({ children, beforeHeader, variant = 'full' }: HeaderProps): ReactElement {
    const { t } = useTranslation('header');
    const headerRef = useRef<HTMLElement>(null);
    const config = useConfig();
    const showChat =
        variant === 'full' &&
        (config.commerceAgent?.enabled === 'true' || config.commerceAgent?.enabled === true) &&
        validateShopperAgentConfig(config.commerceAgent);
    const updateHeaderHeight = useCallback(() => {
        if (headerRef.current) {
            const height = `${headerRef.current.offsetHeight}px`;
            headerRef.current.style.setProperty('--header-height', height);
            document.documentElement.style.setProperty('--header-height', height);
        }
    }, []);

    useEffect(() => {
        const el = headerRef.current;
        if (!el) return;
        updateHeaderHeight();
        const observer = new ResizeObserver(updateHeaderHeight);
        observer.observe(el);
        return () => observer.disconnect();
    }, [updateHeaderHeight]);

    if (variant === 'checkout') {
        return (
            <header ref={headerRef} className="bg-header-background text-header-foreground sticky top-0 z-50">
                <div className="section-container">
                    <div className="flex items-center h-16">
                        <Link to="/" className="flex-shrink-0 flex items-center" data-testid="header-logo">
                            <img
                                src={logo}
                                alt={t('logoAlt')}
                                className="w-[200px] h-auto [filter:var(--header-logo-filter)]"
                            />
                        </Link>
                        <div className="flex-1" />
                        <CartBadge />
                    </div>
                </div>
            </header>
        );
    }

    return (
        <header ref={headerRef} className="bg-header-background text-header-foreground sticky top-0 z-50">
            <div className="bg-header-accent-background text-header-accent-foreground text-[0.8rem] text-center py-2 px-4">
                All purchases benefit the patients of St. Jude Children&apos;s Research Hospital®
            </div>
            <div className="flex justify-end section-container">{beforeHeader}</div>
            {/* Main nav row - solid crimson block on mobile (menu, logo, cart all in white), plain white row on desktop */}
            <div className="bg-header-accent-background lg:bg-transparent">
                <div className="section-container py-6">
                    {/*
                     * One grid drives both breakpoints. Every child (logo, search, nav, donate CTA,
                     * utility icons) mounts once and is repositioned via grid line numbers instead of
                     * being duplicated behind `hidden`/`lg:flex` pairs, or nested inside an extra wrapper
                     * — nesting would break both the grid placement (col/row-start only works on direct
                     * grid children) and the mobile full-width search row (a nested child can never be
                     * wider than its own parent cell).
                     * Mobile (3 cols x 2 rows): row 1 = nav | logo | end-icons, row 2 = search (full width).
                     * Desktop (5 cols x 2 rows): row 1 = logo | search | (—) | end-icons (utility row),
                     * row 2 = (—) | (—) | nav | donate | (—) — the mega menu + Donate CTA sit directly
                     * under Search, forming a second-tier nav row.
                     */}
                    <div className="grid items-center gap-x-2 gap-y-3 grid-cols-[auto_0fr_auto] lg:grid-cols-[auto_0fr_auto_auto_auto] lg:gap-x-6 lg:gap-y-2">
                        {/* Logo - centered white silhouette on mobile (crimson row behind it), plain start-aligned on desktop */}
                        <Link
                            to="/"
                            data-testid="header-logo"
                            className="col-start-2 row-start-1 justify-self-center px-4 py-2 lg:col-start-1 lg:justify-self-start lg:px-0 lg:py-0 sj-child-logo">
                            <img
                                src={logo}
                                alt={t('logoAlt')}
                                className="w-[200px] h-auto [filter:brightness(0)_invert(1)] lg:[filter:var(--header-logo-filter)]"
                            />
                        </Link>

                        {/* Search - white bar full-width row under the crimson mobile top row; on desktop it spans
                            the same two columns as nav + Donate below it and centers itself within that span, so it
                            sits centered above them rather than merely above the flexible spacer column. */}
                        <div
                            data-testid="header-search-row"
                            className="col-start-1 col-span-3 row-start-2 w-full rounded-md bg-header-background px-3 py-2 lg:col-start-3 lg:col-span-2 lg:row-start-1 lg:w-full lg:max-w-md lg:justify-self-center lg:rounded-none lg:bg-transparent lg:px-0 lg:py-0">
                            <LocationKeyedSearch />
                        </div>

                        {/* Navigation - hamburger on mobile (unchanged internally), mega menu on desktop */}
                        <div
                            data-testid="header-nav"
                            className="col-start-1 row-start-1 justify-self-start p-1.5 text-header-accent-foreground lg:col-start-3 lg:row-start-2 lg:p-0 lg:text-header-foreground">
                            {children}
                        </div>

                        {/* Donate Now - end of the navigation matrix, desktop only, same row as the nav */}
                        <a
                            href={DONATE_HREF}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid="header-donate-cta"
                            className="hidden lg:col-start-4 lg:row-start-2 lg:inline-flex items-center justify-center px-5 py-2 rounded-full text-sm font-semibold bg-header-accent-background text-header-accent-foreground transition-all btn-cta-donate">
                            Donate Now
                        </a>

                        {/* Utility icons - white on crimson row on mobile, plain end zone on desktop */}
                        <div
                            data-testid="header-end-actions"
                            className="col-start-3 row-start-1 flex items-center justify-self-end text-header-accent-foreground lg:col-start-5 lg:row-start-1 lg:text-header-foreground">
                            <UITarget targetId="sfcc.header.before.cart" />
                            {showChat && (
                                <Button
                                    variant="ghost"
                                    size="icon"
                                    className="cursor-pointer lg:px-4 px-1 hover:bg-transparent hover:opacity-50 transition-opacity"
                                    onClick={() => launchChat()}
                                    aria-label={t('openChat')}>
                                    <SparklesIcon />
                                </Button>
                            )}
                            <UserActions />
                            <CartBadge />
                        </div>
                    </div>
                </div>
            </div>

            <div className="section-container pb-6">
                <UITarget targetId="sfcc.header.bnpl.banner" />
            </div>
        </header>
    );
}
